import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Target, FileText, Send, BarChart3, Settings2, TrendingUp } from 'lucide-react';
interface SlideGSKProofProps {
  onContinue: () => void;
}
export function SlideGSKProof({
  onContinue
}: SlideGSKProofProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 1000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);
  const engine = [{
    icon: <Target className="w-4 h-4" data-id="element-251" />,
    label: 'Strategy'
  }, {
    icon: <FileText className="w-4 h-4" data-id="element-252" />,
    label: 'Content'
  }, {
    icon: <Send className="w-4 h-4" data-id="element-253" />,
    label: 'Distribution'
  }, {
    icon: <BarChart3 className="w-4 h-4" data-id="element-254" />,
    label: 'Data'
  }, {
    icon: <Settings2 className="w-4 h-4" data-id="element-255" />,
    label: 'Optimization'
  }];
  const regions = [{
    country: 'Nigeria',
    tag: 'Live',
    stats: [{
      label: 'Brands',
      value: '5'
    }, {
      label: 'Campaigns / year',
      value: '4'
    }],
    detail: 'GSK Africa — end-to-end HCP campaigns'
  }, {
    country: 'Kenya',
    tag: 'Live',
    stats: [{
      label: 'Brands',
      value: '5'
    }, {
      label: 'Campaigns / year',
      value: '4'
    }],
    detail: 'GSK Africa — end-to-end HCP campaigns'
  }, {
    country: 'Gulf countries',
    tag: 'Measured',
    highlight: true,
    stats: [{
      label: 'Products',
      value: '3'
    }, {
      label: 'Rx lift',
      value: '+2%'
    }],
    detail: 'Measured incremental Rx uplift on indicated products'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 max-w-7xl mx-auto" data-id="element-256">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-257">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-258">
          Proof it works
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-259">
          This is not theory.{' '}
          <span className="text-teal-400" data-id="element-260">We already run this model.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-261">
          Snakker already operates this engine across multiple regions — with
          measurable engagement, CTA and prescription outcomes.
        </p>
      </motion.div>

      {/* Region cards row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8" data-id="element-262">
        {regions.map((r, idx) => <motion.div key={r.country} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        y: stage >= 1 ? 0 : 16
      }} transition={{
        delay: idx * 0.15
      }} className={`rounded-2xl p-5 border ${r.highlight ? 'bg-teal-400/10 border-teal-400/40 shadow-[0_0_30px_rgba(45,212,191,0.08)]' : 'bg-navy-800/50 border-navy-600'}`} data-id="element-263">
            <div className="flex items-center justify-between mb-4" data-id="element-264">
              <h3 className="text-xl font-serif text-white" data-id="element-265">{r.country}</h3>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full font-medium ${r.highlight ? 'text-navy-900 bg-teal-400 border border-teal-400' : 'text-teal-400 bg-teal-400/10 border border-teal-400/30'}`} data-id="element-266">
                {r.tag}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-267">
              {r.stats.map(s => <div key={s.label} data-id="element-268">
                  <div className={`text-3xl font-serif tracking-tight ${r.highlight ? 'text-teal-400' : 'text-white'}`} data-id="element-269">
                    {s.value}
                  </div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest font-medium mt-0.5" data-id="element-270">
                    {s.label}
                  </div>
                </div>)}
            </div>

            <p className={`text-xs leading-relaxed flex items-start gap-1.5 ${r.highlight ? 'text-teal-300/80' : 'text-white/55'}`} data-id="element-271">
              {r.highlight && <TrendingUp className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" data-id="element-272" />}
              <span data-id="element-273">{r.detail}</span>
            </p>
          </motion.div>)}
      </div>

      {/* Bottom row: engine + bridge */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6 items-stretch" data-id="element-274">
        {/* Campaign engine */}
        <div className="lg:col-span-7" data-id="element-275">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: stage >= 2 ? 1 : 0
        }} className="bg-navy-800/40 border border-navy-700 rounded-2xl p-6 h-full" data-id="element-276">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-5 text-center" data-id="element-277">
              The Campaign Engine — same model, every region
            </p>
            <div className="flex items-center justify-between relative" data-id="element-278">
              <div className="absolute top-5 left-5 right-5 h-px bg-teal-400/20 z-0" data-id="element-279" />
              {engine.map(node => <div key={node.label} className="relative z-10 flex flex-col items-center gap-2" data-id="element-280">
                  <div className="w-10 h-10 rounded-full bg-navy-900 border border-teal-400/40 flex items-center justify-center text-teal-400 shadow-lg" data-id="element-281">
                    {node.icon}
                  </div>
                  <span className="text-white/65 text-[10px] uppercase tracking-wider" data-id="element-282">
                    {node.label}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Roche bridge */}
        <div className="lg:col-span-5" data-id="element-283">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0
        }} transition={{
          delay: 0.3
        }} className="bg-teal-400/5 border border-teal-400/20 rounded-2xl p-6 h-full flex flex-col justify-center" data-id="element-284">
            <p className="text-teal-300 text-[10px] uppercase tracking-widest font-medium mb-4" data-id="element-285">
              Bridge to Roche
            </p>
            <p className="text-xl md:text-2xl font-serif text-white leading-snug" data-id="element-286">
              The same model can support{' '}
              <span className="text-teal-400" data-id="element-287">
                Roche Africa's priority brands
              </span>{' '}
              across West African markets.
            </p>
            <div className="mt-5 pt-4 border-t border-teal-400/20" data-id="element-288">
              <p className="text-white/70 text-xs leading-relaxed" data-id="element-289">
                You already have the content. You already have the channels.
                Snakker makes them work together, learn together and improve
                over time.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="flex justify-end mt-5" data-id="element-290">
        <Button onClick={onContinue} data-id="element-291">Build the partnership →</Button>
      </div>
    </div>;
}